import { CSSProperties, useEffect, useMemo, useState, useRef, MouseEventHandler, useCallback } from 'react';
import { useSelector } from 'react-redux';
import ImageGallery, { ReactImageGalleryItem } from 'react-image-gallery';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from "@fortawesome/free-solid-svg-icons/faTimes";
import { ButtonTypes } from "@iddataweb/idw-common-ui/enums";
import { useImageGalleryItems } from '@hooks/use-image-gallery-items';
import { getLanguageContent } from '@redux/selectors/language-selectors';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { ImageGalleryItemComponent } from './image-gallery-item-component/image-gallery-item-component';
import { ImageGalleryNavComponent } from './image-gallery-nav-component/image-gallery-nav-component';
import styles from "./image-attribute.less";

type ImageAttributeProps = {
	id: string;
	images: Array<string>;
	showGalleryInitially: boolean;
	centerPreviewMessage: boolean;
	previewMessage?: string;
	imageLabels: Array<string>;
};

export const ImageAttribute = (props: ImageAttributeProps) => {
	const {
		id,
		images,
		showGalleryInitially,
		previewMessage,
		centerPreviewMessage,
		imageLabels
	} = props;

	const closePreviewRef = useRef<HTMLButtonElement>(null);
	const { items, isLoadingImages, hasLoadingError } = useImageGalleryItems(images);
	const [showGallery, setShowGallery] = useState(showGalleryInitially);
	const [showNavigation, setShowNavigation] = useState(false);
	const showPlayButton = false;
	const showFullscreenButton = false;
	const [previewImageMessage, setPreviewImageMessage] = useState('');
	const content = useSelector(getLanguageContent);

	const renderItem = (item: ReactImageGalleryItem) => {
		return <ImageGalleryItemComponent item={item} />;
	};

	const renderThumbInner = (item: ReactImageGalleryItem) => {
		return (
			<ImageGalleryItemComponent item={item} />
		);
	};

	const renderNav = (
		onClick: MouseEventHandler<HTMLButtonElement>,
		disabled: boolean,
		className: string,
		ariaLabel: string,
		points: string
	) => {
		const realOnClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			onClick(event);
			if (closePreviewRef?.current) {
				setTimeout(() => {
					closePreviewRef.current.focus();
				}, 100);
			}
		};

		return (
			<ImageGalleryNavComponent
				onClick={realOnClick}
				disabled={disabled}
				className={className}
				ariaLabel={ariaLabel}
				points={points}
			/>
		);
	}

	const renderLeftNav = useCallback((onClick: MouseEventHandler<HTMLButtonElement>, disabled: boolean) => {
		return renderNav(onClick, disabled, "image-gallery-left-nav", "Previous Image", "15 18 9 12 15 6");
	}, []);

	const renderRightNav = useCallback((onClick: MouseEventHandler<HTMLButtonElement>, disabled: boolean) => {
		return renderNav(onClick, disabled, "image-gallery-right-nav", "Next Image", "9 18 15 12 9 6");
	}, []);


	useEffect(() => {
		const imageGalleryElement = document.getElementById(id);

		if (imageGalleryElement) {
			const elements = imageGalleryElement.getElementsByClassName("image-gallery-thumbnail");
			if (elements && elements.length > 0) {
				Array.from(elements).forEach((element, index) => {
					element.ariaLabel = imageLabels[index];
				});
			}
		}
	});

	useEffect(() => {
		if (showGallery && closePreviewRef?.current) {
			setTimeout(() => {
				closePreviewRef.current.focus();
			}, 100);
		}
	}, [showGallery]);

	useEffect(() => {
		const defaultPreviewMessage =
			items.length > 1
				? content.form.imageAttribute.previewTextMultiple
				: content.form.imageAttribute.previewTextSingle;
		if (previewMessage) {
			setPreviewImageMessage(previewMessage);
		} else {
			setPreviewImageMessage(defaultPreviewMessage);
		}
	}, [items, content]);

	const onThumbnailClicked = () => {
		if (!showGallery) {
			setShowGallery(true);
			if (items.length > 1 && !showNavigation) {
				setShowNavigation(true);
			}
		}
	};

	const hideGallery = () => {
		setShowGallery(false);
		setShowNavigation(false);
		const imageGalleryElement = document.getElementById(id);

		if (imageGalleryElement) {
			const elements = imageGalleryElement.getElementsByClassName("image-gallery-thumbnail");
			if (elements && elements.length > 0) {
				(Array.from(elements)[0] as HTMLElement).focus();
			}
		}
	};

	const previewMessageStyle: CSSProperties = {
		textAlign: centerPreviewMessage ? "center" : "left"
	};

	const imageGalleryClassNames = useMemo(() => {
		return showGallery ? `${styles.showGallery} ${styles.fadeIn}` : `${styles.hideGallery} ${styles.fadeOut}`;
	}, [showGallery]);

	return (
		<div id={id} className={styles.imageAttributeContainer}>
			{!showGallery && (
				<div className={styles.previewMessage} style={previewMessageStyle}>
					{previewImageMessage}
				</div>
			)}
			{isLoadingImages && <LoadingSpinner loadingText={'Loading...'} />}
			{!hasLoadingError && !isLoadingImages && (
				<ImageGallery
					additionalClass={imageGalleryClassNames}
					lazyLoad={true}
					infinite={false}
					showNav={showNavigation}
					items={items}
					showPlayButton={showPlayButton}
					showFullscreenButton={showFullscreenButton}
					onThumbnailClick={onThumbnailClicked}
					renderCustomControls={() => {
						return showGallery ? (
							<button
								title={"close preview"}
								type={ButtonTypes.BUTTON}
								ref={closePreviewRef}
								className={styles.imageGalleryCloseButton}
								onClick={hideGallery}
							>
								<FontAwesomeIcon icon={faTimes} className={styles.timesIcon} />
							</button>
						) : null;
					}}
					renderLeftNav={renderLeftNav}
					renderRightNav={renderRightNav}
					renderItem={renderItem}
					renderThumbInner={renderThumbInner}
				/>
			)}
		</div>
	);
};